import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ArrowRight } from "lucide-react";
import { useCart, effectiveUnitPrice } from "../lib/CartContext";
import { useSettings } from "../lib/SettingsContext";
import SEO from "../lib/SEO";

export default function Cart() {
    const { items, updateQty, removeItem, subtotal } = useCart();
    const { settings } = useSettings();
    const rawDeliveryFee = Number(settings?.delivery_fee) || 0;
    const freeMin = Number(settings?.free_delivery_min) || 0;
    const deliveryFee = freeMin > 0 && subtotal >= freeMin ? 0 : rawDeliveryFee;
    const total = subtotal + deliveryFee;

    return (
        <main data-testid="cart-page" className="pt-24 md:pt-[10rem] pb-20 bg-[#FFF7FB] min-h-[60vh]">
            <SEO title="Cart — Cake Central" path="/cart" />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="font-display text-4xl md:text-5xl text-ink mb-2 leading-none">Your Cart</h1>
                <p className="text-ink-soft mb-10">
                    {items.length === 0 ? "Your cart is empty." : `${items.length} item${items.length > 1 ? "s" : ""} in your cart.`}
                </p>

                {items.length === 0 ? (
                    <div className="text-center py-16">
                        <Link to="/shop" className="btn-pink">Browse the shop</Link>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-[1fr_320px] gap-10">
                        <div className="space-y-4">
                            {items.map((item) => {
                                const perUnit = effectiveUnitPrice(item.price, item.price_tiers, item.qty);
                                const lineTotal = perUnit * item.qty;
                                const discounted = perUnit < Number(item.price);
                                return (
                                    <div
                                        key={item.product_id}
                                        className="flex gap-4 bg-white border border-[#EAE3DE] p-4"
                                        data-testid={`cart-item-${item.product_id}`}
                                    >
                                        <Link to={`/shop/product/${item.slug}`} className="shrink-0 w-24 h-24 overflow-hidden bg-blush-pale">
                                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                        </Link>
                                        <div className="flex-1 min-w-0">
                                            <Link to={`/shop/product/${item.slug}`} className="font-display text-lg text-ink hover:text-blush leading-tight">
                                                {item.name}
                                            </Link>
                                            <p className="text-sm text-ink-soft mt-1">
                                                ${perUnit.toFixed(2)} AUD each
                                                {discounted && <span className="ml-2 text-blush text-xs tracking-[0.1em] uppercase">Bulk price</span>}
                                            </p>

                                            <div className="mt-3 flex items-center justify-between gap-3">
                                                <div className="inline-flex items-center border border-[#EAE3DE]">
                                                    <button
                                                        onClick={() => updateQty(item.product_id, item.qty - 1)}
                                                        className="px-2 py-1 text-ink hover:text-blush"
                                                        aria-label="Decrease"
                                                    >
                                                        <Minus size={12} />
                                                    </button>
                                                    <span className="px-4 text-sm">{item.qty}</span>
                                                    <button
                                                        onClick={() => updateQty(item.product_id, item.qty + 1)}
                                                        className="px-2 py-1 text-ink hover:text-blush"
                                                        aria-label="Increase"
                                                    >
                                                        <Plus size={12} />
                                                    </button>
                                                </div>
                                                <p className="font-display text-base">${lineTotal.toFixed(2)}</p>
                                                <button
                                                    onClick={() => removeItem(item.product_id)}
                                                    className="text-ink-soft hover:text-blush"
                                                    aria-label="Remove"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <aside className="bg-white border border-[#EAE3DE] p-6 h-fit sticky top-32">
                            <h2 className="font-display text-2xl mb-5">Summary</h2>
                            <div className="flex justify-between text-sm py-2">
                                <span className="text-ink-soft">Subtotal</span>
                                <span className="font-medium">${subtotal.toFixed(2)} AUD</span>
                            </div>
                            <div className="flex justify-between text-sm py-2">
                                <span className="text-ink-soft">Delivery</span>
                                <span className="font-medium">
                                    {rawDeliveryFee > 0
                                        ? (deliveryFee === 0
                                            ? <span className="text-emerald-600">FREE</span>
                                            : `$${deliveryFee.toFixed(2)} AUD`)
                                        : "Calculated at checkout"}
                                </span>
                            </div>
                            {freeMin > 0 && subtotal < freeMin && (
                                <p className="text-[11px] text-blush mt-1">
                                    Add ${(freeMin - subtotal).toFixed(2)} more for free delivery
                                </p>
                            )}
                            <div className="h-px bg-[#EAE3DE] my-4" />
                            <div className="flex justify-between font-display text-lg">
                                <span>Total</span>
                                <span>${total.toFixed(2)}</span>
                            </div>
                            <Link to="/checkout" className="btn-pink w-full justify-center mt-6" data-testid="cart-checkout-btn">
                                Checkout <ArrowRight size={14} />
                            </Link>
                            <Link to="/shop" className="block text-center mt-4 text-xs tracking-[0.2em] uppercase text-ink-soft hover:text-blush">
                                Continue shopping
                            </Link>
                        </aside>
                    </div>
                )}
            </div>
        </main>
    );
}
